import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";

/**
 * CC-064 (2026-09-25) — Password reset landing page.
 *
 * Reads the reset token from `?token=…` in the URL, prompts for a
 * new password (with confirmation), POSTs to /auth/reset-password.
 * On success, redirects to /login after a short delay so the user
 * can visually confirm the change stuck.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Reset failed. Request a new link from the sign-in screen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cc-landing min-h-screen flex flex-col" data-testid="reset-password-page">
      <div className="max-w-[1200px] w-full mx-auto px-8 py-5 flex items-center justify-between">
        <Link to="/"><Logo withWordmark tone="dark" /></Link>
        <Link to="/login" className="text-[12.5px] text-[color:var(--cc-navy-muted)] hover:text-white">
          ← Back to sign-in
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[440px]">
          <div className="text-[11px] uppercase tracking-[.18em] text-[#ffc72c] font-semibold mb-3">
            Password reset
          </div>
          <h1 className="font-display font-extrabold text-[34px] leading-tight tracking-tight text-white">
            Choose a new password.
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-navy-muted)] mt-2">
            {token
              ? "Enter and confirm a new password below. Once you save, you'll be redirected to sign in."
              : "This link is missing its reset token. Request a new one from the sign-in screen."}
          </p>

          {done ? (
            <div
              className="mt-6 bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] rounded-2xl p-6"
              data-testid="reset-password-done"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-[#7dd3a4] shrink-0 mt-[2px]" />
                <div>
                  <div className="text-white text-[14.5px] font-semibold">Password updated.</div>
                  <p className="text-[color:var(--cc-navy-muted)] text-[13px] mt-1">
                    Redirecting to sign-in…
                  </p>
                </div>
              </div>
            </div>
          ) : token ? (
            <form
              onSubmit={submit}
              className="mt-6 bg-[color:var(--cc-navy-card)]/70 backdrop-blur border border-[color:var(--cc-navy-border)] rounded-2xl p-6"
              data-testid="reset-password-form"
            >
              <label className="block text-[11px] uppercase tracking-[.12em] text-[color:var(--cc-navy-muted)] font-semibold mb-2">
                New password (min 8 characters)
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                data-testid="reset-password-new-input"
                className="w-full bg-[#0a0f1a] border border-[color:var(--cc-navy-border)] rounded-lg px-3.5 py-3 text-[14px] text-white outline-none focus:border-[#0072bc] transition-colors"
                placeholder="••••••••"
              />
              <label className="block text-[11px] uppercase tracking-[.12em] text-[color:var(--cc-navy-muted)] font-semibold mt-4 mb-2">
                Confirm new password
              </label>
              <input
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-password-confirm-input"
                className="w-full bg-[#0a0f1a] border border-[color:var(--cc-navy-border)] rounded-lg px-3.5 py-3 text-[14px] text-white outline-none focus:border-[#0072bc] transition-colors"
                placeholder="••••••••"
              />
              {err && (
                <div
                  className="mt-4 flex items-start gap-2 text-[12.5px] text-[#ff9d9d] bg-[#3a1414] border border-[#5c1e1e] rounded-md px-3 py-2"
                  data-testid="reset-password-error"
                >
                  <AlertCircle size={14} strokeWidth={1.8} className="mt-[1px] shrink-0" />
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit-btn"
                className="cc-btn-primary w-full justify-center mt-6 disabled:opacity-60"
              >
                {busy ? "Saving…" : (<>Set new password <ArrowRight size={16} strokeWidth={1.8} /></>)}
              </button>
            </form>
          ) : (
            <div
              className="mt-6 flex items-start gap-2 text-[12.5px] text-[#ff9d9d] bg-[#3a1414] border border-[#5c1e1e] rounded-md px-3 py-2"
              data-testid="reset-password-missing-token"
            >
              <AlertCircle size={14} strokeWidth={1.8} className="mt-[1px] shrink-0" />
              Missing reset token. <Link to="/login" className="ml-1 underline">Go to sign-in</Link>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
